import {
  doc,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
} from 'firebase/firestore';
import { fireStoreDB } from 'services/firebaseConfig';
import { sendVerificationEmail } from './resendEmail';

const OTP_EXPIRY_MINUTES = 15;

const generateOtp = () => {
  const array = new Uint32Array(1);
  crypto.getRandomValues(array);
  return String(array[0] % 1000000).padStart(6, '0');
};

// Cooldown: don't re-send if a code was created within this many minutes
const RESEND_COOLDOWN_MINUTES = 2;

export const generateAndSendOtpCode = async (uid, email, force = false) => {
  // Check for an existing valid code — skip sending if one was created recently
  if (!force) {
    try {
      const existing = await getDoc(doc(fireStoreDB, 'emailVerifications', uid));
      if (existing.exists()) {
        const data = existing.data();
        // Already verified — no need to send another code
        if (data.verified) {
          return false;
        }
        const createdAt = new Date(data.createdAt);
        const cooldownExpiry = new Date(createdAt.getTime() + RESEND_COOLDOWN_MINUTES * 60 * 1000);
        if (new Date() < cooldownExpiry) {
          // A fresh code was sent very recently — skip to avoid duplicates
          return false;
        }
      }
    } catch (readErr) {
      console.error('[OTP] Firestore read failed:', readErr.code, readErr.message);
      // Continue to send — don't block verification on a read failure
    }
  }

  const otp = generateOtp();
  const expiresAt = new Date(
    Date.now() + OTP_EXPIRY_MINUTES * 60 * 1000
  ).toISOString();

  try {
    await setDoc(doc(fireStoreDB, 'emailVerifications', uid), {
      token: otp,
      email,
      verified: false,
      expiresAt,
      createdAt: new Date().toISOString(),
      failedAttempts: 0,
    });
  } catch (writeErr) {
    console.error('[OTP] Firestore write failed:', writeErr.code, writeErr.message);
    throw writeErr;
  }

  try {
    await sendVerificationEmail(email, otp);
  } catch (emailErr) {
    console.error('[OTP] Email send failed:', emailErr.message);
    throw emailErr;
  }

  return true;
};

export const verifyOtpCode = async (uid, otp) => {
  const docRef = doc(fireStoreDB, 'emailVerifications', uid);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return { success: false, message: 'No verification code found. Please request a new one.' };
  }

  const { token: storedOtp, expiresAt, verified, failedAttempts = 0 } = docSnap.data();

  // Rate limiting: block after 5 failed attempts
  if (failedAttempts >= 5) {
    return { success: false, message: 'Too many failed attempts. Please request a new code.' };
  }

  // Already verified
  if (verified) {
    if (timingSafeEqual(otp, storedOtp)) {
      return { success: true };
    }
    return { success: false, message: 'Invalid code.' };
  }

  if (new Date() > new Date(expiresAt)) {
    return { success: false, message: 'This code has expired. Please request a new one.' };
  }

  if (!timingSafeEqual(otp, storedOtp)) {
    await updateDoc(docRef, { failedAttempts: failedAttempts + 1 });
    const remaining = 4 - failedAttempts;
    return {
      success: false,
      message: remaining > 0
        ? `Incorrect code. ${remaining} attempt${remaining === 1 ? '' : 's'} remaining.`
        : 'Too many failed attempts. Please request a new code.',
    };
  }

  await updateDoc(docRef, { verified: true, verifiedAt: new Date().toISOString() });
  return { success: true };
};

// Timing-safe string comparison to prevent timing attacks
const timingSafeEqual = (a, b) => {
  if (typeof a !== 'string' || typeof b !== 'string') return false;
  if (a.length !== b.length) return false;
  let result = 0;
  for (let i = 0; i < a.length; i++) {
    result |= a.charCodeAt(i) ^ b.charCodeAt(i);
  }
  return result === 0;
};

export const subscribeToVerification = (uid, onVerified) => {
  const docRef = doc(fireStoreDB, 'emailVerifications', uid);
  return onSnapshot(docRef, (snap) => {
    if (snap.exists() && snap.data().verified === true) {
      onVerified();
    }
  });
};
